import * as React from "react";
import { Link } from "react-router-dom";

import { Card, Typography, Image } from "antd";
import {
  ImageDescription,
  IndexList,
  List,
  Marker,
  Note,
  ProgressCircle,
} from "../../common";

const list: List[] = [
  {
    title: "オンライン詐欺の基本",
  },
  {
    title: "オンライン詐欺を見抜く",
    children: [
      {
        title: "詐欺メール",
        children: [{ title: "メールアドレスの入手方法" }],
      },
      {
        title: "詐欺サイト",
      },
    ],
  },
];

const { Text, Title } = Typography;
export const OnlineFraud = () => {
  return (
    <div>
      <ProgressCircle />
      <IndexList list={list} />
      <Title level={3}>
        <span id="オンライン詐欺の基本">１．オンライン詐欺の基本</span>
      </Title>
      <Image
        src="https://4.bp.blogspot.com/-2HtNpopd8j4/WcB5u7qYQMI/AAAAAAABG14/S1HuOpthvscS6jSr1PRCfxThHwX0DFJCwCLcBGAs/s400/sagishi_man.png"
        preview={false}
      />
      <br />
      詐欺の基本は人間の心の隙を突くソーシャルエンジニアリングです。
      「ネームドロップ（権威があるよう
      に見せて聞き出す）」「ハリーアップ（急がせて聞き出す）」など、
      相手が正常に判断できない状況に追い込んで必要な情報を聞き出したり、相手に自分が求める行動を行わせたりするものがあります。
      どれだけセキュリティを強化していても、人の心の隙を突けば情報を簡単に抜き取られることがあります。
      ここではソーシャルエンジニアリングの手口とその対策を学びます。
      <br />
      <br />
      まず詐欺の基本を見ていきましょう。
      <Marker>
        ネームドロップ
        <Note
          title="ネームドロップ"
          content="業務上の上司や立場が上の人間を装って要求を実行させるソーシャルエンジニアリングの手法。"
        />
        は立場が上の人間を装う
      </Marker>
      ソーシャルエンジニアリングの手法です。
      攻撃者は警察や上司のふりをして金銭や情報を要求します。
      人間は権威に弱いため、社長を名乗る攻撃者に対して情報を渡してしまうことがあります。
      次に
      <Marker>
        ハリーアップ
        <Note
          title="ハリーアップ"
          content="ソーシャルエンジニアリングの手法で、相手を急かすことで正常な判断をできなくなるようにして、目的の要求を通すこと。"
        />
        は相手を急かすことで正常な判断をできなくさせる
      </Marker>
      ソーシャルエンジニアリングの手法です。
      事件・事故に巻き込まれたので緊急でお金が必要または明日までに支払わないと法的措置にうつる架空請求など、とにかく焦らせるための嘘をつきます。
      「オレオレ詐欺」「振込詐欺」でもよく使われています。
      <Marker>
        ギブアンドテイク
        <Note
          title="ギブアンドテイク"
          content="ソーシャルエンジニアリングの手法で、相手になにかのメリットを与えることで、その代償として自分の目的の情報を引き出す手法。"
        />
        は相手のメリットを与える代わりに情報などを手に入れる
      </Marker>
      ソーシャルエンジニアリングの手法です。
      <Marker>
        フレンドシップ
        <Note
          title="ギブアンドテイク"
          content="ソーシャルエンジニアリングの手法で、友情を持って接することで要求を断りにくくする。"
        />
        は友達のふりをしたり友情をもって接することで要求を通りやすくさせる
      </Marker>
      ソーシャルエンジニアリングの手法です。
      たとえば「会社のコンピュータでウイルスを検知したのでメンテナンスソフトを導入したほうがいい」というアドバイスをしますが、
      実際にはマルウェアをインストールしようとしている場面が考えられます。
      親切にアドバイスをもらえた後だと、有難く従ってしまいます。
      ここまでいくつか名前の付いた手法を紹介しましたが、これ以外にも人の心の隙はどこにでも散らばっています。
      特に裸の自撮り写真やアダルトサイト関連などは当事者が周りに相談しづらいような手口も多く一人で抱え込んでしまい間違った決断をしてしまうこともあります。
      「他の人もやっているから」「一万人に一人の当選者」などの言葉にも注意をして、常に自分で事実を確認して行動しましょう。
      <Title level={3}>
        <span id="オンライン詐欺を見抜く">２．オンライン詐欺を見抜く</span>
      </Title>
      オンライン詐欺を見抜くためには心の隙を作らないことが何よりも助けになりますが、技術的に詐欺を見抜く方法も存在します。
      ここでは、オンライン詐欺を見抜くための方法を学びます。
      <Title level={4}>
        <span id="詐欺メール">詐欺メール</span>
      </Title>
      <ImageDescription
        src="https://i.imgur.com/bfWSKDI.png"
        description={
          <>
            引用：
            <a
              href="https://www.nisc.go.jp/security-site/files/handbook-all.pdf"
              target="_blank"
              rel="noreferrer"
            >
              内閣サイバーセキュリティセンター／インターネットの安全・安心ハンドブック／38ページ
            </a>
          </>
        }
      />
      フィッシング詐欺はメールやSMSなどを使って偽のサイトなどに誘導する詐欺のことです。
      phishingはsophistecated（洗練された）とfishing（釣る）を混ぜた造語で、英単語から分かるように嘘の情報を餌にして釣り上げるというイメージです。
      従来のフィッシング詐欺といえばEメール経由のものがメインでしたが現在はLINEなどのメッセージアプリやゲーム内のメッセージ機能など、
      ありとあらゆるプラットフォームで行われています。
      <ImageDescription
        src="https://i.imgur.com/bHlpWSk.png"
        description={
          <>
            引用：
            <a
              href="https://www.nisc.go.jp/security-site/files/handbook-all.pdf"
              target="_blank"
              rel="noreferrer"
            >
              内閣サイバーセキュリティセンター／インターネットの安全・安心ハンドブック／37ページ
            </a>
          </>
        }
      />
      基本的にソーシャルエンジニアリングの手法を使ってくることが多いため、一見まともな文章に見えてしまいます。
      たとえばこのフィッシングメッセージは翌日失効するという文面で受信者を焦らせる「ハリーアップ」の手口が使われています。
      実際、不特定多数に送られるスパムメールはよく見ると不審な部分が多く日本語の文が不自然な場合もあります。
      この場合はURLの終わり方が<Text code>.jp</Text>ではなく
      <Text code>.gq</Text>
      と、見慣れない怪しいドメインを使っていることが分かります。 他にも
      <Text code>twitter.com</Text>の代わりに<Text code>tvvitter.com</Text>
      のような正しそうに見えるURLが使われることもあります。
      不審なリンクをクリックせず、まずはブックマークをした公式サイトで似たようなアナウンスがないか確認したり、
      他にも被害者がいるであろうスパムメールの場合は文章ごとウェブで検索するのも効果的です。
      <br />
      <br />
      従来の不特定多数に送られてきたスパムメールに対して、近年はライフルのように
      <Marker>
        少数を狙う標的型メールも増えたため真偽を見極めるのが難しくなってきています。
      </Marker>
      標的型メールでは攻撃者が相手の名前、所属、身分、同じような会社でやりとりするメールのパターンなどを入手して、
      通常の仕事のメールと見分けがつかないほど精緻なものを送ってきます。
      <ul>
        <li>リンクを確認する</li>
        <li>公式サイトを確認する</li>
        <li>文章を確認する</li>
        <li>送信者を確認する</li>
      </ul>
      詐欺メールを見抜くためにはとにかく細かい部分で、自分で事実確認をすることが大事です。
      特にリンクをクリックするだけでマルウェアに感染してしまうような攻撃もあるため、
      不審なものは無視して公式であると確認できないときは常に疑う意識をもちましょう。
      またサービスの流出事故の際にパスワードリセット通知を送ったり災害時に公的機関を装ったメールを送るなど、
      緊急事態で判断力が低下しているときにこそフィッシング詐欺が回るのも事実として覚えておきましょう。
      <Card>
        <Title level={5}>
          <span id="メールアドレスの入手法">メールアドレスの入手法</span>
        </Title>
        スパムメールや標的型メールなど様々な手段でフィッシングが行われていますが、
        「そもそも攻撃者はなんでわたしのメールアドレスを知っているの？」という疑問が残ります。
        <ul>
          <li>登録しているサービスの情報流出した</li>
          <li>登録しているサービスが個人情報を売った</li>
          <li>総当たりでメールアドレスを入力した</li>
          <li>友人がマルウェアに感染し友人の連絡先から情報が伝わった</li>
        </ul>
        以前メールアドレスの流出を経験したことがある場合はそこからメールアドレスを入手しています。
        またダークウェブと呼ばれるインターネットの闇サイトで個人情報が売買されている可能性もあります。
        総当たりで入力されたメールアドレスは<Text code>tom@gmail.com</Text>
        など短いものが主流になります。
        またマルウェア感染すると連絡先に入っている個人情報までも流出してしまうことも考えられるため、
        セキュリティ対策には自分だけではないという強い責任をもちましょう。
        <br />
        <br />
        内閣サイバーセキュリティセンターは解決策として
        <Marker>
          知り合いや友達と連絡するためのメールアドレスと
          ウェブサービスや通信販売サイトなどに登録するメールアドレスの使い分ける
        </Marker>
        ことを推奨しています。
        さらにサービス毎に別々のアドレスを登録する方式ならどのサービスから攻撃者へ情報が流出しているかを調べることができます。
      </Card>
      <Title level={4}>
        <span id="詐欺サイト">詐欺サイト</span>
      </Title>
      フィッシングメールの先にはマルウェア感染させるものだけでなく、詐欺サイト（フィッシングサイト）に誘導させるものが多く存在します。
      詐欺サイトを見極めるためにはEV-SSL証明書を確認することが大事です。（
      <Link to="/lectures/device-security#さらに安全なEV-SSL証明書の登場">
        デバイスセキュリティーさらに安全なEV-SSL証明書の登場
      </Link>
      ）
      また詐欺メールのリンクと同様に詐欺サイトのURLも一見正しそうに見えるそれらしいものが多いので、
      URLを見るだけでも詐欺サイトを見抜くことができます。
      しかし実際にすべての公式サイトのURLを覚えるのは不可能なので、
      特に金融関係の処理をする場合は
      <Marker>
        公式サイトをブックマークして、直接ブックマークからサイトにいくようにすると安全です。
      </Marker>
    </div>
  );
};

export default OnlineFraud;
